/**
 * Copyright (c) Mayoral Moda Infantil, S.A.U. and affiliates.
 */

import { LinearProgress, styled } from '@mui/material';
import { Router } from 'next/router';
import { FC, useEffect, useRef, useState } from 'react';

import { RouterProgressDelay } from '@/lib/constants/router';

type OwnerState = {
  loading: boolean;
};

const RouterProgressRoot = styled('div', {
  name: 'RouterProgress',
  slot: 'Root',
})<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  visibility: 'hidden',
  pointerEvents: 'none',
  zIndex: theme.zIndex.modal,
  '&::after': {
    content: '""',
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: theme.palette.background.paper,
    opacity: 0.42,
    zIndex: -1,
  },
  ...(ownerState.loading && {
    visibility: 'visible',
    pointerEvents: 'auto',
  }),
}));

const RouterProgress: FC = () => {
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const handleStart = () => {
    // If the timer is already running, do not start a new one.
    if (timer.current) {
      return;
    }

    timer.current = setTimeout(() => {
      setLoading(true);
    }, RouterProgressDelay);
  };

  const handleComplete = () => {
    if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }

    setLoading(false);
  };

  useEffect(() => {
    // Attach routing events
    Router.events.on('routeChangeStart', handleStart);
    Router.events.on('routeChangeComplete', handleComplete);
    Router.events.on('routeChangeError', handleComplete);

    return () => {
      if (timer.current) {
        clearInterval(timer.current);
        timer.current = null;
      }

      Router.events.off('routeChangeStart', handleStart);
      Router.events.off('routeChangeComplete', handleComplete);
      Router.events.off('routeChangeError', handleComplete);
    };
  }, []);

  const ownerState: OwnerState = { loading };

  return (
    <RouterProgressRoot ownerState={ownerState} aria-hidden="true" data-testid="router-progress">
      <LinearProgress variant="indeterminate" />
    </RouterProgressRoot>
  );
};

export default RouterProgress;
